import React from "react";
import classNames from "classnames";
import AsyncSelect from "react-select/async";
import FormGroup from "../FormGroup.js";
import FormLabel from "../FormLabel.js";
import FormInputGroup from "../FormInputGroup.js";
import { MultiValue, SingleValue } from "react-select";
import { useLazyFetchCalendarsListQuery } from "../../../services/graph";
import { GraphCalendarType } from "../../../types/GraphCalendarType";

export type UserCalendarSelectProps = {
    className?: any
    error?: Error | string | undefined
    isClearable?: boolean
    isDisabled?: boolean
    isMulti?: boolean
    label?: string
    onChange: (v: MultiValue<SelectOptionData<string, GraphCalendarType>> | SingleValue<SelectOptionData<string, GraphCalendarType>>) => void
    query?: any,
    userId: string
    value: string | string[] | null
};

export const UserCalendarSelect = React.forwardRef<any, UserCalendarSelectProps>(({
    className,
    isClearable = false,
    isDisabled = false,
    isMulti = false,
    label = "Calendar",
    onChange,
    query = useLazyFetchCalendarsListQuery,
    userId,
    value
}: Readonly<UserCalendarSelectProps>, ref) => {
    const componentId = React.useId();
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [load, { data }] = query();

    const buildDefaultValue = () : SelectOptionData<string, GraphCalendarType> | SelectOptionData<string, GraphCalendarType>[] | null => {
        if (!value || !data) {
            return null;
        }

        const selectedValue = value;
        const results = data as GraphCalendarType[];

        const resultSets : SelectOptionData<string, GraphCalendarType>[] = [
            ...results.map((el) => ({ label: el.displayName, value: el.id, data: el, isDisabled: false } as SelectOptionData<string, GraphCalendarType>))
        ];

        if (resultSets.length === 0) {
            return null;
        }

        if (isMulti === true) {
            const selectedValues : string[] = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
            return resultSets.filter(({ value }) => selectedValues.includes(value));
        }

        const foundResult = resultSets.find(({ value }) => value === selectedValue) ?? null;
        return foundResult;
    };

    const loadOptions = (input: string, callback: (options: SelectOptionData<string, GraphCalendarType>[]) => void) => {
        const newCacheKey = new Date().valueOf();
        load({ userId }).then(
            ({ data }: { data: GraphCalendarType[] }) => {
                if (data && data.length > 0) {
                    const records: SelectOptionData<string, GraphCalendarType>[] = data.map(el => ({
                        label: el.displayName,
                        value: el.id,
                        data: el,
                        isDisabled: false,
                        isFixed: false
                    } as SelectOptionData<string, GraphCalendarType>));
                    callback(records);
                }
                else {
                    callback([]);
                }
            }
        )
        .finally(() => {
            setCacheKey(() => newCacheKey);
        });
    };

    const displayValue = React.useMemo(() => buildDefaultValue(), [ data, isMulti, value ]);
    return (
        <FormGroup>
            <FormLabel htmlFor={componentId}>{label}</FormLabel>
            <FormInputGroup>
                <AsyncSelect
                    key={userId}
                    id={componentId}
                    className={classNames('flex-fill', className)}
                    ref={ref}
                    cacheOptions={true}
                    defaultOptions={true}
                    loadOptions={loadOptions}
                    isClearable={isClearable}
                    isDisabled={isDisabled}
                    isMulti={isMulti}
                    value={displayValue}
                    onChange={(e) => onChange(e)}
                />
            </FormInputGroup>
        </FormGroup>
    );
});